import { Injectable, signal } from "@angular/core";

/**
 * Tracks WHICH price ids have had their Source of Fund (`isSof`) toggle
 * changed by the author in this edit session.
 *
 * It exists because a price round-trips whole on every save. The toggle shows
 * a default for a price that has never had a value (true for prepaid, false
 * for postpaid), and a value the catalog published rides along from the GET.
 * Sending either back would mint an enricher-owned entry nobody chose, and
 * that entry then shadows the catalog's own value for good. So `isSof` goes on
 * the wire only for the ids in this set — see `stripUntouchedIsSof` in the
 * save coordinator.
 *
 * Provided in `EnrichmentEditPackage`'s `providers` (NOT root) and not held in
 * `EnrichmentPricing`, for the reason spelled out on
 * {@link EnrichmentPriceLockStore}: the Prices tab is destroyed on every tab
 * switch while the edited value lives on in the model, so a component-local
 * flag would forget the edit and the next save would strip it.
 *
 * A successful save of a price removes it from the set: what the author chose
 * is now the stored value.
 */
@Injectable()
export class EnrichmentIsSofEditStore {
  private readonly ids = signal<ReadonlySet<string>>(new Set());

  /** Read-only signal of the edited price ids. Read it (not {@link isEdited})
   *  from a `computed` so the read is tracked. */
  readonly editedIds = this.ids.asReadonly();

  /** Untracked point read — for the save coordinator. */
  isEdited(id: string | undefined): boolean {
    return !!id && this.ids().has(id);
  }

  markEdited(id: string): void {
    if (!id || this.ids().has(id)) return;
    const next = new Set(this.ids());
    next.add(id);
    this.ids.set(next);
  }

  clearEdited(id: string): void {
    if (!id || !this.ids().has(id)) return;
    const next = new Set(this.ids());
    next.delete(id);
    this.ids.set(next);
  }

  clear(): void {
    if (this.ids().size === 0) return;
    this.ids.set(new Set());
  }
}
